import React from "react";
import { observer } from "mobx-react";
import Loader from "../../../shared/Loader/Loader";
import className from "../../../shared/className";
import { useStore } from "../../../AppStore";
import { PatientStore } from "../../Patients";
import DockedList from "./DockedList";
import { HistoryContext } from "../../../routes";
import { MonitorContext } from "../Monitor";

import Swal from "../../../shared/Swal";
import GroupSort from "../../Patients/GroupSort";
import { openUnitFilterModal } from "../../Patients/UnitFilter";
import "./PmPatientList.scss";

function PmPatientList({ parent }) {
  const appStore = useStore();

  if (!appStore.patientStore) {
    appStore.patientStore = new PatientStore({ appStore });
  }
  const { patientStore } = appStore;
  if (!patientStore.patientItemTextModel)
    patientStore.patientItemTextModel = patientItemTextModel;

  React.useEffect(() => {
    return () => {
      patientStore.patientItemTextModel = undefined;
    };
  }, [patientStore]);

  const { openGroupSort, openUnitFilter } = React.useMemo(() => {
    const openGroupSort = (e) => {
      e.preventDefault();
      patientStore.modalLoading = false;
      if (patientStore.groupSortDisabled) {
        return;
      }
      Swal.fire({
        title: "Group / Sort",
        html: <GroupSort store={patientStore} />,
        showConfirmButton: false,
      });
    };

    const openUnitFilter = (e) => {
      e.preventDefault();
      openUnitFilterModal({ patientStore });
    };
    return { openGroupSort, openUnitFilter };
  }, [patientStore]);

  return (
    <DockedList
      className="pm-patient-list"
      search={(value) => {
        patientStore.searchQuery = value;
        const { filterResult } = patientStore;
        const { total, totalForText } = filterResult;
        return { valid: !!(total && !totalForText) };
      }}
      query={patientStore.searchQuery}
      title={patientStore.selectedListOption?.label}
    >
      <nav className="patient-list-nav sub-nav">
        <a
          {...className({
            disabled: !patientStore.unitFilterEnabled,
            active: patientStore.unitFilterActive,
          })}
          href="filter-unit"
          onClick={openUnitFilter}
        >
          Filter Units
        </a>

        <a
          {...className({
            disabled: patientStore.groupSortDisabled,
            active:
              !patientStore.groupSortDisabled && patientStore.groupSortActive,
          })}
          href="group-sort"
          onClick={openGroupSort}
        >
          Group / Sort
        </a>
      </nav>

      {patientStore.loading ? (
        <Loader />
      ) : (
        <StickyList {...{ patientStore, parent }} />
      )}
    </DockedList>
  );
}

export function StickyList({ patientStore }) {
  const stickyList = React.useRef(null);
  React.useLayoutEffect(() => {
    stickyList.current.scrollTo(0, StickyList.scrollTop);
  });
  return (
    <div
      className="sticky-list"
      ref={stickyList}
      onScroll={(e) => {
        StickyList.scrollTop = e.currentTarget.scrollTop;
      }}
    >
      <PatientsResult patientStore={patientStore} />
    </div>
  );
}

function PatientsResult({ patientStore }) {
  const { patients, filterResult, groupsWithPatients, searchQuery } =
    patientStore;
  let warning = null;
  if (filterResult.total) {
    if (filterResult.totalForUnits < filterResult.total) {
      warning = (
        <div
          className="warning btn"
          onClick={() => openUnitFilterModal({ patientStore })}
        >
          Modify Unit Filters to see more search results.
        </div>
      );
    }
    if (searchQuery?.trim() && !filterResult.patients.length) {
      return (
        <>
          {warning}
          <div className="warning">No Results for Search Criteria</div>
        </>
      );
    }
  }
  if (groupsWithPatients) {
    return [
      warning,
      groupsWithPatients?.map((group) => (
        <ListSegment key={group.label || " "} group={group} parent={parent} />
      )),
    ];
  }
  return [
    warning,
    patients?.map((patient) => (
      <ListItem patient={patient} key={patient.asmpid} parent={parent} />
    )),
  ];
}

function patientItemTextModel(patient) {
  if (!patient) return {};
  let gender = patient.gender?.[0];
  if (!["M", "F"].includes(gender)) gender = "U";
  return {
    name: `${patient.lastName}, ${patient.firstName}`,
    gender: patient.gender?.[0],
    age: patient.age,
    mrn: patient.mrn,
    location: patient.moduleSpecifics?.location,
    dob: patient.dateOfBirth,
    unit: patient.moduleSpecifics?.unit,
  };
}

function ListItem({ patient }) {
  const history = React.useContext(HistoryContext);
  const textModel = patientItemTextModel(patient);
  const monitor = React.useContext(MonitorContext);
  const selectPatient = ({ event, patient }) => {
    if (patient.isEmptyBed) return;
    StickyList.scrollTop = event.target.offsetParent.scrollTop;
    history.push(`/patient/${patient.asmpid}/monitor`);
  };
  return (
    <div
      {...className({
        patient: true,
        selected: monitor.asmpid === patient.asmpid,
        "status-active": patient.status === "active",
        "not-connected": patient.isEmptyBed,
      })}
      onClick={(event) => selectPatient({ event, patient })}
    >
      <div className="row">
        {patient.isEmptyBed ? (
          <div className="phi">NO PATIENT CONNECTED</div>
        ) : (
          <div className="phi">
            <span className="name">{textModel.name}</span> •{" "}
            <span>{textModel.gender}</span> • <span>{textModel.age}</span>
          </div>
        )}
        <div className="location">{textModel.location}</div>
      </div>
      {!patient.isEmptyBed && (
        <div className="mrn">
          {textModel.mrn} • DOB: {textModel.dob}
        </div>
      )}
    </div>
  );
}

function ListSegment({ group, parent }) {
  if (!group.patients?.length) return null;
  return (
    <>
      <header>{group.label || "Not Documented"}</header>
      {group.patients?.filter(Boolean).map((item) => (
        <ListItem key={item.asmpid} patient={item} parent={parent} />
      ))}
    </>
  );
}

export default observer(PmPatientList);
